import { useState } from 'react';

import { Emoji } from 'emoji-mart';

import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import { addEmoji } from '@/utils/addEmoji';
import { useIsElementFocused } from '@/utils/hooks/utils/useIsElementFocused';
import { useValidateCaption, ValidateReturnType } from '@/utils/hooks/utils/useValidateCaption';

import type { CaptionRestriction, SocialProfile } from '@/components/composite/Caption/types';

import {
    CaptionRestrictionsType,
    CaptionType
} from '@/lib/captionsHashtagLists/captionRestrictions';

interface UseCaptionInputProps {
    caption: CaptionType;
    updateCaption: (caption: string) => void;
    validateCaptionCallback?: (caption: ValidateReturnType) => void;
    initialRestrictions?: CaptionRestrictionsType[];
    forcedSocialProfile?: SocialProfile | null;
    rootId?: string;
    inputProps?: Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'style'>;
}

export const useCaptionInput = ({
    caption,
    updateCaption,
    validateCaptionCallback,
    initialRestrictions = [],
    forcedSocialProfile = null,
    rootId,
    inputProps
}: UseCaptionInputProps) => {
    const { t } = useTranslation();
    const { activeSocialProfile } = useActiveSocialProfile();
    const [cursorPosition, setCursorPosition] = useState<number>(0);
    const [captionRestrictions, setCaptionRestrictions] = useState<
        CaptionRestriction[] | undefined
    >(initialRestrictions);
    const { wrapperRef, isFocused, setIsFocused } = useIsElementFocused(rootId);

    useValidateCaption(
        caption,
        initialRestrictions,
        validateCaptionCallback,
        setCaptionRestrictions
    );

    const currentProfile = forcedSocialProfile ?? activeSocialProfile;

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
        setCursorPosition(e.target.selectionStart ?? 0);
        inputProps?.onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        updateCaption(e.target.value);
        inputProps?.onChange?.(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
        setIsFocused(true);
        inputProps?.onFocus?.(e);
    };

    const handleEmojiSelect = (emoji: typeof Emoji.Props) => {
        addEmoji(emoji, caption, updateCaption, cursorPosition, setCursorPosition);
    };

    return {
        t,
        currentProfile,
        cursorPosition,
        setCursorPosition,
        captionRestrictions,
        wrapperRef,
        isFocused,
        setIsFocused,
        handleBlur,
        handleChange,
        handleFocus,
        handleEmojiSelect
    };
};
